import { supabaseDataProvider } from "ra-supabase-core";
import {
  withLifecycleCallbacks,
  type CreateParams,
  type DataProvider,
  type GetListParams,
  type Identifier,
  type UpdateParams,
} from "ra-core";

import type {
  Contact,
  ContactNote,
  Deal,
  DealNote,
  RAFile,
  Sale,
  SalesFormData,
  SignUpData,
} from "../../types";
import { getActivityLog } from "../commons/activity";
import { getCompanyAvatar } from "../commons/getCompanyAvatar";
import { getContactAvatar } from "../commons/getContactAvatar";
import { getIsInitialized } from "./authProvider";
import { extractServopaStatuses } from "./servopaFilters";
import { supabase } from "./supabase";
import { caminhoDoAnexo } from "./organizacao";
import { fonteParaLeitura } from "../../commons/somenteLeitura";
import { lerEscopoDePapel, limparEscopoEmCache } from "../../commons/useRoleDataScope";

/**
 * Ambiente sem Supabase configurado é configuração quebrada e tem que doer —
 * esta trava fica. A exceção é o modo local sem login
 * (`components/app/ModoLocalSemLogin.tsx`), onde este arquivo é carregado por
 * ser importado estaticamente pelo CRM, mas o provider que roda é outro, o
 * falso. Sem a exceção, o modo local morria aqui antes de desenhar qualquer
 * coisa: a tela ficava no spinner e parecia pedido de login.
 */
const modoLocal = import.meta.env.DEV && import.meta.env.VITE_MODO_LOCAL === "1";

if (!modoLocal && import.meta.env.VITE_SUPABASE_URL === undefined) {
  throw new Error("Please set the VITE_SUPABASE_URL environment variable");
}
if (!modoLocal && import.meta.env.VITE_SUPABASE_ANON_KEY === undefined) {
  throw new Error("Please set the VITE_SUPABASE_ANON_KEY environment variable");
}

const providerPostgrest = supabaseDataProvider({
  instanceUrl: import.meta.env.VITE_SUPABASE_URL ?? "http://localhost:1",
  apiKey: import.meta.env.VITE_SUPABASE_ANON_KEY ?? "sem-chave-no-modo-local",
  supabaseClient: supabase,
  sortOrder: "asc,desc.nullslast" as any,
});

// O provider do ra-supabase fala com o PostgREST por URL, sem passar por
// `supabase.from` — então a troca pelas views dos perfis de leitura (migration
// 184) precisa acontecer aqui também. Só nas leituras: escrita desses perfis o
// banco recusa em qualquer tabela.
const baseDataProvider: typeof providerPostgrest = {
  ...providerPostgrest,
  getList: (resource, params) => providerPostgrest.getList(fonteParaLeitura(resource), params),
  getOne: (resource, params) => providerPostgrest.getOne(fonteParaLeitura(resource), params),
  getMany: (resource, params) => providerPostgrest.getMany(fonteParaLeitura(resource), params),
  getManyReference: (resource, params) =>
    providerPostgrest.getManyReference(fonteParaLeitura(resource), params),
};

const getAuthHeaders = async () => {
  const apikey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  const { data: sessionData } = await supabase.auth.getSession();
  let accessToken = sessionData.session?.access_token;

  if (!accessToken) {
    const { data: refreshed } = await supabase.auth.refreshSession();
    accessToken = refreshed.session?.access_token;
  }

  if (!accessToken) {
    throw new Error("Missing auth session");
  }

  return {
    Authorization: `Bearer ${accessToken}`,
    apikey,
  };
};

type AccessScope = {
  authUserId: string | null;
  isSuperAdmin: boolean;
  /** Administrador / responsável pela unidade (308): venda pela unidade dela. */
  porUnidade: boolean;
  scopedCrmUserIds: number[];
  scopedAuthUserIds: string[];
};

const ACCESS_SCOPE_CACHE_TTL_MS = 10_000;

let cachedAccessScope: AccessScope | null = null;
let cachedAccessScopeExpiresAt = 0;
let accessScopePromise: Promise<AccessScope> | null = null;

const isUuidLike = (value: unknown) =>
  typeof value === "string" && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);

// Genérico no builder: os chamadores passam um PostgrestFilterBuilder (já
// encadeado com .eq()), não o PostgrestQueryBuilder cru de supabase.from() —
// e `.or()` só existe no primeiro.
const applyContactsSummaryOrFilter = <Q extends { or: (filters: string) => Q }>(
  query: Q,
  orFilter: unknown,
): Q => {
  if (!orFilter || typeof orFilter !== "object") {
    return query;
  }

  const clauses = Object.entries(orFilter as Record<string, unknown>)
    .map(([key, value]) => {
      const [field, operator] = key.split("@");
      if (!field || operator !== "ilike") {
        return null;
      }

      const searchValue = String(value ?? "").trim();
      if (!searchValue) {
        return null;
      }

      return `${field}.ilike.%${searchValue}%`;
    })
    .filter((clause): clause is string => Boolean(clause));

  return clauses.length > 0 ? query.or(clauses.join(",")) : query;
};

const getContactsSummaryList = async (
  resource: "contacts" | "clients",
  params: GetListParams,
  scope: AccessScope,
) => {
  const filter = {
    ...getScopedListFilter(resource, params.filter as Record<string, unknown> | undefined, scope),
  } as Record<string, unknown>;

  let query = supabase
    .from("contacts_summary")
    .select("*", { count: "exact" })
    .order(params.sort?.field || "last_seen", {
      ascending: params.sort?.order !== "DESC",
    });

  if (filter.user_id !== undefined) {
    query = query.eq("user_id", Number(filter.user_id));
  }

  if (typeof filter["user_id@in"] === "string") {
    const ids = parseIdList(filter["user_id@in"]);
    if (ids.length === 0) {
      return { data: [], total: 0 };
    }
    query = query.in("user_id", ids);
  }

  if (resource === "clients" || filter.has_sales === true) {
    query = query.eq("has_sales", true);
  }

  // Contato arquivado (transferido, limpeza de duplicado — migration 240) sai
  // da lista de Contatos e de quem busca contato para vender; o filtro
  // "Arquivados/transferidos" mostra só eles, para o dono antigo achar o
  // histórico. Clientes mantém todos: a venda continua sendo do dono antigo.
  if (resource === "contacts") {
    query = filter.arquivados === true
      ? query.not("arquivado_em", "is", null)
      : query.is("arquivado_em", null);
  }

  if (filter["tags@cs"] !== undefined) {
    const tagIds = parseIdList(filter["tags@cs"]);
    if (tagIds.length === 0) {
      return { data: [], total: 0 };
    }
    query = query.contains("tags", tagIds);
  }

  if (filter.id != null && Number(filter.id) !== -1) {
    query = query.eq("id", Number(filter.id));
  }

  query = applyContactsSummaryOrFilter(query, filter["@or"]);

  return fetchPaginated(query, params.pagination);
};

const isScopedResource = (resource: string) =>
  resource === "contacts"
  || resource === "clients"
  || resource === "deals"
  || resource === "servopa_vendas"
  || resource === "servopa_vendas_with_details"
  || resource === "proposal_simulations";

const getParentScopeConfig = (resource: string) => {
  if (resource === "contactNotes" || resource === "tasks") {
    return { parentResource: "contacts" as const, parentField: "contact_id" };
  }

  if (resource === "dealNotes") {
    return { parentResource: "deals" as const, parentField: "deal_id" };
  }

  if (resource === "servopa_lances") {
    return { parentResource: "servopa_vendas" as const, parentField: "venda_id" };
  }

  return null;
};

const isScopedMutationResource = (resource: string) =>
  isScopedResource(resource)
  || getParentScopeConfig(resource) !== null;

const normalizeResourceForBaseAccess = (resource: string) => {
  if (resource === "clients") return "contacts";
  if (resource === "servopa_vendas_with_details") return "servopa_vendas";
  return resource;
};

// Apply react-admin page/perPage pagination to a PostgREST query, run it, and
// shape the react-admin getList result. (Was duplicated across getList paths.)
async function fetchPaginated(
  query: any,
  pagination: { page?: number; perPage?: number } | undefined,
): Promise<{ data: any[]; total: number }> {
  const page = pagination?.page || 1;
  const perPage = pagination?.perPage || 25;
  const { data, error, count } = await query.range((page - 1) * perPage, page * perPage - 1);
  if (error) throw error;
  return { data: data || [], total: count ?? (data || []).length };
}

const parseIdList = (value: unknown): number[] => {
  if (Array.isArray(value)) {
    return value
      .map((item) => Number(item))
      .filter((item) => Number.isFinite(item));
  }

  if (typeof value !== "string") {
    return [];
  }

  return value
    .replace(/[(){}\s]/g, "")
    .split(",")
    .map((item) => Number(item))
    .filter((item) => Number.isFinite(item));
};

const parseStringList = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value
      .map((item) => String(item).trim())
      .filter(Boolean);
  }

  if (typeof value !== "string") {
    return [];
  }

  const normalized = value.trim().replace(/^\(/, "").replace(/\)$/, "");
  if (!normalized) {
    return [];
  }

  const matches = normalized.match(/"([^"]+)"|[^,]+/g) ?? [];
  return matches
    .map((item) => item.replace(/^"|"$/g, "").trim())
    .filter(Boolean);
};

const intersectIds = (left: number[], right: number[]) => {
  const rightSet = new Set(right);
  return left.filter((item) => rightSet.has(item));
};

// Sessão guardada no navegador, sem ida ao /auth/v1/user: o id serve para
// montar filtros, e quem barra o acesso de verdade é a RLS com o token.
const getCurrentAuthUserId = async () => {
  const { data: authData, error: authError } = await supabase.auth.getSession();
  if (authError || !authData?.session) {
    return null;
  }

  return authData.session.user.id;
};

const createAccessDeniedError = () => {
  const error = new Error("Acesso negado: registro fora do escopo de acesso.");
  Object.assign(error, {
    status: 403,
    statusText: "Forbidden",
  });
  return error;
};

const clearAccessScopeCache = () => {
  limparEscopoEmCache();
  cachedAccessScope = null;
  cachedAccessScopeExpiresAt = 0;
  accessScopePromise = null;
};

supabase.auth.onAuthStateChange(() => {
  clearAccessScopeCache();
});

const resolveCurrentCrmUserId = async (authUserId: string): Promise<number | null> => {
  const readOwnCrmUserId = async () => {
    const { data } = await supabase
      .from("users")
      .select("id")
      .eq("user_id", authUserId)
      .maybeSingle();
    return data?.id ?? null;
  };

  let crmUserId = await readOwnCrmUserId();
  if (crmUserId) {
    return crmUserId;
  }

  try {
    await supabase.functions.invoke("ensure-crm-user", {
      method: "POST",
    });
  } catch {
    try {
      // Backward-compatible fallback when ensure-crm-user is not deployed.
      await supabase.functions.invoke("users", {
        method: "GET",
      });
    } catch {
      // Keep existing behavior if fallback endpoint is unavailable.
    }
  }

  crmUserId = await readOwnCrmUserId();
  return crmUserId;
};

// Mesma leitura do useRoleDataScope (is_super_admin, is_manager, leitura_perfil,
// users, recorte do gerente), compartilhada: antes a tela fazia as duas, uma
// depois da outra. Perfis de leitura (migration 184) vêm com isSuperAdmin e
// escopo vazio, porque as views leitura_* já chegam recortadas no servidor.
const computeAccessScope = async (): Promise<AccessScope> => {
  const escopo = await lerEscopoDePapel();
  if (!escopo) {
    return {
      authUserId: null,
      isSuperAdmin: false,
      porUnidade: false,
      scopedCrmUserIds: [],
      scopedAuthUserIds: [],
    };
  }

  return {
    authUserId: escopo.currentAuthUserId,
    isSuperAdmin: escopo.isSuperAdmin,
    porUnidade: escopo.porUnidade,
    scopedCrmUserIds: escopo.scopedCrmUserIds,
    scopedAuthUserIds: escopo.scopedAuthUserIds,
  };
};

const getAccessScope = async (): Promise<AccessScope> => {
  const now = Date.now();

  if (cachedAccessScope && cachedAccessScopeExpiresAt > now) {
    return cachedAccessScope;
  }

  if (accessScopePromise) {
    return accessScopePromise;
  }

  accessScopePromise = computeAccessScope()
    .then((scope) => {
      cachedAccessScope = scope;
      cachedAccessScopeExpiresAt = Date.now() + ACCESS_SCOPE_CACHE_TTL_MS;
      return scope;
    })
    .finally(() => {
      accessScopePromise = null;
    });

  return accessScopePromise;
};

const getScopedListFilter = (resource: string, rawFilter: Record<string, unknown> | undefined, scope: AccessScope) => {
  const filter = { ...(rawFilter || {}) };

  if (scope.isSuperAdmin) {
    return filter;
  }

  if (resource === "contacts" || resource === "clients") {
    if (scope.scopedCrmUserIds.length === 0) {
      return { ...filter, user_id: -1 };
    }

    // O filtro por vendedor agora é exposto pra gerente (vê o time). Respeita uma
    // escolha que esteja DENTRO do escopo; sem escolha, mostra o escopo inteiro.
    // Out-of-scope vira user_id=-1 (nega), em vez de cair pro escopo todo.
    const requestedOwner = filter.user_id;
    delete filter.user_id;
    delete filter["user_id@in"];

    if (requestedOwner !== undefined && requestedOwner !== null && String(requestedOwner) !== "all") {
      const ownerId = Number(requestedOwner);
      return scope.scopedCrmUserIds.includes(ownerId)
        ? { ...filter, user_id: ownerId }
        : { ...filter, user_id: -1 };
    }

    return { ...filter, "user_id@in": `(${scope.scopedCrmUserIds.join(",")})` };
  }

  if (resource === "servopa_vendas" || resource === "servopa_vendas_with_details") {
    // Venda de quem mudou de unidade fica na unidade de origem (308): sem
    // vendedor escolhido, quem vê por unidade fica com o recorte da RLS.
    if (scope.porUnidade && filter.sales_person_id === undefined && filter.sales_person_ids === undefined) {
      return filter;
    }
    if (scope.scopedCrmUserIds.length === 0) {
      return { ...filter, sales_person_id: -1 };
    }

    if (filter.sales_person_id !== undefined) {
      const salesPersonId = Number(filter.sales_person_id);
      return scope.scopedCrmUserIds.includes(salesPersonId)
        ? filter
        : { ...filter, sales_person_id: -1 };
    }

    if (filter.sales_person_ids !== undefined) {
      const requestedIds = parseIdList(filter.sales_person_ids);
      const allowedIds = intersectIds(requestedIds, scope.scopedCrmUserIds);
      return allowedIds.length > 0
        ? { ...filter, sales_person_ids: allowedIds }
        : { ...filter, sales_person_id: -1 };
    }

    return { ...filter, sales_person_ids: scope.scopedCrmUserIds };
  }

  if (resource === "proposal_simulations") {
    if (scope.scopedAuthUserIds.length === 0) {
      return { ...filter, created_by: "__forbidden__" };
    }

    if (scope.scopedAuthUserIds.length === 1) {
      return { ...filter, created_by: scope.scopedAuthUserIds[0] };
    }

    return { ...filter, "created_by@in": `(${scope.scopedAuthUserIds.join(",")})` };
  }

  return filter;
};

const isRecordInScope = (resource: string, record: any, scope: AccessScope) => {
  if (!scope.authUserId) return false;
  if (scope.isSuperAdmin) return true;

  if (resource === "contacts" || resource === "clients") {
    const rawOwner = record?.user_id;
    if (rawOwner == null || rawOwner === "") {
      return false;
    }

    // Defensive compatibility: some forms may temporarily pass auth UUID instead of CRM numeric id.
    if (isUuidLike(rawOwner)) {
      return scope.scopedAuthUserIds.includes(String(rawOwner));
    }

    const ownerUserId = Number(rawOwner);
    return Number.isFinite(ownerUserId) && scope.scopedCrmUserIds.includes(ownerUserId);
  }

  if (resource === "servopa_vendas" || resource === "servopa_vendas_with_details") {
    const salesPersonId = record?.sales_person_id == null ? null : Number(record.sales_person_id);
    return !!salesPersonId && scope.scopedCrmUserIds.includes(salesPersonId);
  }

  if (resource === "proposal_simulations") {
    const createdBy = typeof record?.created_by === "string" ? record.created_by : null;
    return !!createdBy && scope.scopedAuthUserIds.includes(createdBy);
  }

  return true;
};

const getExistingRecordForMutation = async (resource: string, id: Identifier) => {
  const baseResource = normalizeResourceForBaseAccess(resource);
  const result = await baseDataProvider.getOne(baseResource, { id });
  return result.data;
};

const filterChildRecordsByScope = async (
  resource: string,
  records: any[],
  scope: AccessScope,
) => {
  const config = getParentScopeConfig(resource);
  if (!config) {
    return records;
  }

  if (!scope.authUserId) {
    return [];
  }

  if (scope.isSuperAdmin) {
    return records;
  }

  const parentIds = Array.from(
    new Set(
      records
        .map((record) => record?.[config.parentField])
        .filter((id) => id != null),
    ),
  );

  if (parentIds.length === 0) {
    return [];
  }

  const parentResult = await baseDataProvider.getMany(config.parentResource, {
    ids: parentIds,
  });

  const allowedParentIds = new Set(
    (parentResult.data || [])
      .filter((record: any) => isRecordInScope(config.parentResource, record, scope))
      .map((record: any) => record.id),
  );

  return records.filter((record) => allowedParentIds.has(record?.[config.parentField]));
};

const assertParentRecordInScope = async (
  parentResource: "contacts" | "deals" | "servopa_vendas",
  parentId: Identifier | null | undefined,
  scope: AccessScope,
) => {
  if (parentId == null) {
    throw createAccessDeniedError();
  }

  const parentRecord = await getExistingRecordForMutation(parentResource, parentId);
  if (!isRecordInScope(parentResource, parentRecord, scope)) {
    throw createAccessDeniedError();
  }
};

const assertMutationInScope = async (
  resource: string,
  scope: AccessScope,
  data?: any,
  previousData?: any,
) => {
  if (!scope.authUserId) {
    throw createAccessDeniedError();
  }

  if (scope.isSuperAdmin) {
    return;
  }

  if (resource === "proposal_simulations") {
    // Arquivar/alterar proposta é ação do DONO: só o próprio criador pode (um
    // gerente vê a proposta do time mas não mexe na de outro). Super admin já
    // retornou acima. Espelha a policy "update own" no banco (migration 70).
    const recordCreatedBy = data?.created_by ?? previousData?.created_by ?? scope.authUserId;
    if (!recordCreatedBy || String(recordCreatedBy) !== String(scope.authUserId)) {
      throw createAccessDeniedError();
    }
    return;
  }

  if (isScopedResource(resource)) {
    const mergedRecord = { ...(previousData || {}), ...(data || {}) };
    if (!isRecordInScope(resource, mergedRecord, scope)) {
      throw createAccessDeniedError();
    }
    return;
  }

  if (resource === "tasks" || resource === "contactNotes" || resource === "proposal_simulations") {
    const contactId = data?.contact_id ?? previousData?.contact_id;
    await assertParentRecordInScope("contacts", contactId, scope);
    return;
  }

  if (resource === "dealNotes") {
    const dealId = data?.deal_id ?? previousData?.deal_id;
    await assertParentRecordInScope("deals", dealId, scope);
  }

  if (resource === "servopa_lances") {
    const vendaId = data?.venda_id ?? previousData?.venda_id;
    await assertParentRecordInScope("servopa_vendas", vendaId, scope);
  }
};

const assertRecordInScope = async (resource: string, record: any) => {
  const scope = await getAccessScope();

  if (!isRecordInScope(resource, record, scope)) {
    throw createAccessDeniedError();
  }
};

const processCompanyLogo = async (params: any) => {
  let logo = params.data.logo;

  if (typeof logo !== "object" || logo === null || !logo.src) {
    logo = await getCompanyAvatar(params.data);
  } else if (logo.rawFile instanceof File) {
    await uploadToBucket(logo);
  }

  return {
    ...params,
    data: {
      ...params.data,
      logo,
    },
  };
};

async function processContactAvatar(
  params: UpdateParams<Contact>,
): Promise<UpdateParams<Contact>>;

async function processContactAvatar(
  params: CreateParams<Contact>,
): Promise<CreateParams<Contact>>;

async function processContactAvatar(
  params: CreateParams<Contact> | UpdateParams<Contact>,
): Promise<CreateParams<Contact> | UpdateParams<Contact>> {
  const { data } = params;
  const email = (data as any)?.details?.email as string | undefined;
  if (data.avatar?.src || !email) {
    return params;
  }
  const avatarUrl = await getContactAvatar(data);

  // Clone the data and modify the clone
  const newData = { ...data, avatar: { src: avatarUrl || undefined } };

  return { ...params, data: newData };
}

const dataProviderWithCustomMethods = {
  ...baseDataProvider,
  async create(resource: string, params: any) {
    if (resource === "tags") {
      const authUserId = await getCurrentAuthUserId();
      if (!authUserId) {
        throw createAccessDeniedError();
      }

      params = {
        ...params,
        data: {
          ...params.data,
          user_id: authUserId,
        },
      };
    }

    // Notas e tarefas guardam o autor como users.id (bigint), mas o formulário
    // manda identity.id, que é o id do login (UUID): o banco recusava a nota.
    if (
      (resource === "contactNotes" || resource === "dealNotes" || resource === "tasks")
      && isUuidLike(params?.data?.user_id)
    ) {
      const crmUserId = await resolveCurrentCrmUserId(params.data.user_id);
      if (!crmUserId) {
        throw createAccessDeniedError();
      }
      params = { ...params, data: { ...params.data, user_id: crmUserId } };
    }

    if (resource === "users") {
      clearAccessScopeCache();
    }

    if (isScopedMutationResource(resource)) {
      let scope = await getAccessScope();

      // Ensure scoped users can create contacts/clients even when the form
      // does not explicitly provide owner user_id.
      const normalizedResource = normalizeResourceForBaseAccess(resource);
      if (
        !scope.isSuperAdmin
        && (normalizedResource === "contacts" || resource === "clients")
      ) {
        let resolvedCrmUserId: number | null = scope.scopedCrmUserIds[0] ?? null;

        // If user_id is coming as auth UUID, map it to CRM numeric id before scope checks.
        if (!resolvedCrmUserId && isUuidLike(params?.data?.user_id)) {
          const { data: mappedByUuid } = await supabase
            .from("users")
            .select("id")
            .eq("user_id", params.data.user_id)
            .maybeSingle();
          if (mappedByUuid?.id) {
            resolvedCrmUserId = mappedByUuid.id;
          }
        }

        if (!resolvedCrmUserId && scope.authUserId) {
          resolvedCrmUserId = await resolveCurrentCrmUserId(scope.authUserId);
        }

        if (resolvedCrmUserId && !scope.scopedCrmUserIds.includes(resolvedCrmUserId)) {
          scope = {
            ...scope,
            scopedCrmUserIds: Array.from(new Set([resolvedCrmUserId, ...scope.scopedCrmUserIds])),
          };
        }

        if (params?.data?.user_id == null && resolvedCrmUserId) {
          params = {
            ...params,
            data: {
              ...params.data,
              user_id: resolvedCrmUserId,
            },
          };
        } else if (isUuidLike(params?.data?.user_id) && resolvedCrmUserId) {
          params = {
            ...params,
            data: {
              ...params.data,
              user_id: resolvedCrmUserId,
            },
          };
        }
      }

      if (
        !scope.isSuperAdmin
        && (normalizedResource === "contacts" || resource === "clients")
        && (params?.data?.user_id == null)
        && scope.scopedCrmUserIds.length > 0
      ) {
        params = {
          ...params,
          data: {
            ...params.data,
            user_id: scope.scopedCrmUserIds[0],
          },
        };
      }

      await assertMutationInScope(resource, scope, params.data);
    }

    return baseDataProvider.create(normalizeResourceForBaseAccess(resource), params);
  },

  async getList(resource: string, params: GetListParams) {
    if (resource === "tags") {
      const authUserId = await getCurrentAuthUserId();
      if (!authUserId) {
        return { data: [], total: 0 };
      }

      let query = supabase
        .from("tags")
        .select("*", { count: "exact" })
        .eq("user_id", authUserId)
        .order(params.sort?.field || "name", { ascending: params.sort?.order !== "DESC" });

      const filter = params.filter as Record<string, unknown> | undefined;
      const searchTerm = typeof filter?.q === "string" ? filter.q.trim() : "";

      if (filter?.id != null && Number(filter.id) !== -1) {
        query = query.eq("id", Number(filter.id));
      }

      if (filter?.name != null) {
        query = query.eq("name", String(filter.name));
      }

      if (filter?.["name@in"] != null) {
        const names = parseStringList(filter["name@in"]);
        if (names.length === 0) {
          return { data: [], total: 0 };
        }
        query = query.in("name", names);
      }

      if (searchTerm) {
        query = query.ilike("name", `%${searchTerm}%`);
      }

      return fetchPaginated(query, params.pagination);
    }

    if (resource === "users") {
      // Bypass PostgREST (forbidden for regular users): query via Supabase client.
      // Apply common filters so callers like SaleModal can find a specific user row.
      let query = supabase
        .from("users")
        .select("*", { count: "exact" })
        .order(params.sort?.field || "name", { ascending: params.sort?.order !== "DESC" });
      const filter = params.filter as Record<string, unknown> | undefined;
      const searchTerm = typeof filter?.q === "string" ? filter.q.trim() : "";
      if (filter?.user_id != null) {
        query = query.eq("user_id", filter.user_id as string);
      }
      if (filter?.id != null && Number(filter.id) !== -1) {
        query = query.eq("id", Number(filter.id));
      }
      if (searchTerm) {
        query = query.ilike("name", `%${searchTerm}%`);
      }

      const page = params.pagination?.page || 1;
      const perPage = params.pagination?.perPage || 25;
      const start = (page - 1) * perPage;
      const end = page * perPage - 1;

      const { data, count } = await query.range(start, end);
      return { data: data || [], total: count ?? (data || []).length };
    }

    const scope = isScopedResource(resource) ? await getAccessScope() : null;
    const scopedParams = scope
      ? {
          ...params,
          filter: getScopedListFilter(resource, params.filter as Record<string, unknown> | undefined, scope),
        }
      : params;

    if (resource === "proposal_simulations") {
      let query = supabase
        .from("proposal_simulations")
        .select("*", { count: "exact" })
        .order(params.sort?.field || "generated_at", {
          ascending: params.sort?.order !== "DESC",
        });

      if (scopedParams.filter?.contact_id !== undefined) {
        const contactId = scopedParams.filter.contact_id;
        // Lista (ex.: persistência em Clientes): eq com array vira "eq.1,2,3" e o banco recusa.
        query = Array.isArray(contactId)
          ? query.in("contact_id", contactId)
          : query.eq("contact_id", contactId);
      }

      if (scopedParams.filter?.created_by) {
        query = query.eq("created_by", scopedParams.filter.created_by);
      }

      if (scopedParams.filter?.["created_by@in"] && typeof scopedParams.filter["created_by@in"] === "string") {
        const ids = String(scopedParams.filter["created_by@in"]).replace(/[(){}\s]/g, "").split(",").filter(Boolean);
        if (ids.length === 0) {
          return { data: [], total: 0 };
        }
        query = query.in("created_by", ids);
      }

      const start = params.pagination?.page
        ? (params.pagination.page - 1) * (params.pagination.perPage || 25)
        : 0;
      const end = params.pagination?.page
        ? params.pagination.page * (params.pagination.perPage || 25) - 1
        : 24;

      const { data, error, count } = await query.range(start, end);
      if (error) throw error;

      return {
        data: data || [],
        total: count ?? data?.length ?? 0,
      };
    }

    if (resource === "companies") {
      return baseDataProvider.getList("companies_summary", scopedParams);
    }
    if (resource === "contacts" || resource === "clients") {
      return getContactsSummaryList(resource, scopedParams, scope!);
    }

    // Handle deals with contact_ids array filter
    if (resource === "deals" && scopedParams.filter?.contact_ids) {
      const contactId = scopedParams.filter.contact_ids;
      // Use Supabase direct query for array containment
      const query = supabase
        .from('deals')
        .select('*', { count: 'exact' })
        .contains('contact_ids', [contactId])
        .order(params.sort?.field || 'created_at', { 
          ascending: params.sort?.order !== 'DESC' 
        });

      const { data, error } = await query.range(
          params.pagination?.page ? (params.pagination.page - 1) * (params.pagination.perPage || 25) : 0,
          params.pagination?.page ? params.pagination.page * (params.pagination.perPage || 25) - 1 : 24
        );
      
      if (error) throw error;
      
      return {
        data: data || [],
        total: data?.length || 0,
      };
    }

    const searchTerm = typeof scopedParams.filter?.q === "string" ? scopedParams.filter.q.trim() : "";
    
    // Handle servopa sales queries (both pending and completed tabs)
    if (resource === "servopa_vendas" || resource === "servopa_vendas_with_details") {
      let query = supabase
        .from(resource as "servopa_vendas")
        .select("*", { count: "exact" })
        .order(params.sort?.field || "created_at", {
          ascending: params.sort?.order !== "DESC",
        });

      // Filter by status (supports status, status@in and status_in)
      const statuses = extractServopaStatuses(scopedParams.filter as Record<string, unknown> | undefined);
      if (statuses && statuses.length > 0) {
        if (statuses.length === 1) {
          query = query.eq("status", statuses[0]);
        } else {
          query = query.in("status", statuses);
        }
      }

      // Filter by id (used by the venda detail page and the ?sale_id deep-link).
      // id is a UUID, so pass it through as-is — no numeric coercion.
      if (scopedParams.filter?.id !== undefined && scopedParams.filter?.id !== null) {
        query = query.eq("id", scopedParams.filter.id);
      }

      // Filter by contact_id when provided
      if (scopedParams.filter?.contact_id !== undefined) {
        const contactId = scopedParams.filter.contact_id;
        // Lista (ex.: persistência em Clientes): eq com array vira "eq.1,2,3" e o banco recusa.
        query = Array.isArray(contactId)
          ? query.in("contact_id", contactId)
          : query.eq("contact_id", contactId);
      }

      // Filter by sales_person_id
      if (scopedParams.filter?.sales_person_id !== undefined) {
        const salesPersonId = scopedParams.filter.sales_person_id;
        if (salesPersonId === null) {
          query = query.is("sales_person_id", null);
        } else if (Number(salesPersonId) === -1) {
          // Explicit deny-all marker for out-of-scope requests.
          query = query.eq("sales_person_id", -1);
        } else {
          query = query.eq("sales_person_id", salesPersonId as any);
        }
      }

      // Filter by many salespeople (used for manager scopes)
      if (scopedParams.filter?.sales_person_ids !== undefined) {
        const salesPersonIds = Array.isArray(scopedParams.filter.sales_person_ids)
          ? scopedParams.filter.sales_person_ids
          : [];

        if (salesPersonIds.length === 0) {
          query = query.eq("sales_person_id", -1);
        } else {
          query = query.in("sales_person_id", salesPersonIds);
        }
      }

      // Filtro por grupo — usado pelo filtro de segmento (Imóvel/Móvel) da aba
      // de Vendas. O segmento não existe nesta view: quem o guarda é
      // servopa_groups, então a tela traduz o segmento na lista de grupos dele
      // e filtra por grupo. Lista vazia é "nenhum grupo neste segmento", ou
      // seja, nenhuma venda — nunca "todas" (mesma convenção de
      // sales_person_ids acima).
      if (scopedParams.filter?.group_numbers !== undefined) {
        const groupNumbers = Array.isArray(scopedParams.filter.group_numbers)
          ? scopedParams.filter.group_numbers.map((g: unknown) => String(g))
          : [];

        if (groupNumbers.length === 0) {
          query = query.eq("group_number", "__sem_grupo__");
        } else {
          query = query.in("group_number", groupNumbers);
        }
      }

      // Filter by contrato if specified
      if (scopedParams.filter?.contrato) {
        query = query.eq("contrato", scopedParams.filter.contrato);
      }

      // Filter by contemplado (boolean) — usado pela aba "Contemplados" e para
      // EXCLUIR contempladas da aba "Efetivadas". Aceita boolean ou 'eq.true'/'eq.false'.
      if (scopedParams.filter?.contemplado !== undefined) {
        const v = scopedParams.filter.contemplado;
        const bool = typeof v === "string" ? v.replace(/^eq\./, "") === "true" : !!v;
        query = query.eq("contemplado", bool);
      }

      // Filter by cancel_kind ('churn' | 'nao_confirmada') — sub-filtro da aba Cancelados.
      if (scopedParams.filter?.cancel_kind !== undefined) {
        const v = scopedParams.filter.cancel_kind;
        const val = typeof v === "string" && v.startsWith("eq.") ? v.slice(3) : v;
        if (val) query = query.eq("cancel_kind", val as string);
      }

      // Filtro por período (venda_data) — usado pelo filtro de data da página de
      // Vendas e pelos cards do dashboard (ex: "Vendas no mês" deep-linka com o
      // mesmo intervalo usado pra contar o KPI, pra bater com a lista exibida).
      if (typeof scopedParams.filter?.["venda_data@gte"] === "string") {
        query = query.gte("venda_data", scopedParams.filter["venda_data@gte"] as string);
      }
      if (typeof scopedParams.filter?.["venda_data@lte"] === "string") {
        query = query.lte("venda_data", scopedParams.filter["venda_data@lte"] as string);
      }

      // Filter by next_lance_status (4-category UI: OK / Pendente / Falha / Não disponível).
      // The 'eq.X' value pattern is the convention used by other filters in this codebase.
      if (scopedParams.filter?.next_lance_status !== undefined) {
        const value = scopedParams.filter.next_lance_status;
        if (typeof value === "string" && value.startsWith("eq.")) {
          query = query.eq("next_lance_status", value.slice(3));
        } else if (value !== null && value !== undefined) {
          query = query.eq("next_lance_status", value as any);
        }
      }
      if (typeof scopedParams.filter?.["next_lance_status@in"] === "string") {
        const list = String(scopedParams.filter["next_lance_status@in"])
          .replace(/[(){}\s]/g, "")
          .split(",")
          .filter(Boolean);
        if (list.length > 0) {
          query = query.in("next_lance_status", list);
        }
      }
      if (scopedParams.filter?.["next_lance_status@is"] === null) {
        query = query.is("next_lance_status", null);
      }

      // Search by name or contract
      if (searchTerm) {
        query = query.or(
          `nome_consorciado.ilike.%${searchTerm}%,contrato.ilike.%${searchTerm}%`,
        );
      }

      const start = params.pagination?.page
        ? (params.pagination.page - 1) * (params.pagination.perPage || 25)
        : 0;
      const end = params.pagination?.page
        ? params.pagination.page * (params.pagination.perPage || 25) - 1
        : 24;

      const { data, error, count } = await query.range(start, end);

      if (error) throw error;

      return {
        data: data || [],
        total: count ?? data?.length ?? 0,
      };
    }

    if (getParentScopeConfig(resource)) {
      const scope = await getAccessScope();
      const result = await baseDataProvider.getList(resource, scopedParams);
      const filteredData = await filterChildRecordsByScope(resource, result.data || [], scope);
      return {
        ...result,
        data: filteredData,
        total: filteredData.length,
      };
    }

    return baseDataProvider.getList(resource, scopedParams);
  },
  async getOne(resource: string, params: any) {
    if (resource === "tags") {
      const authUserId = await getCurrentAuthUserId();
      if (!authUserId) {
        throw createAccessDeniedError();
      }

      const { data, error } = await supabase
        .from("tags")
        .select("*")
        .eq("id", params.id)
        .eq("user_id", authUserId)
        .maybeSingle();

      if (error) throw error;
      if (!data) throw createAccessDeniedError();

      return { data };
    }

    if (resource === "companies") {
      return baseDataProvider.getOne("companies_summary", params);
    }
    // NOTE: contacts getOne uses the table directly, not the view
    // This allows Create/Edit/Show to work properly with full data access
    if (resource === "users") {
      // For sales table, params.id is the auth user_id (UUID)
      // We need to query by user_id instead of id (bigint)
      const { data, error } = await supabase
        .from("users")
        .select("*")
        .eq("user_id", params.id)
        .limit(1);

      if (error) {
        throw new Error(error.message || "Failed to load user");
      }

      const existing = data?.[0];
      if (!existing) {
        const { data: authData, error: authError } =
          await supabase.auth.getUser();
        const authUser = authData?.user;
        if (authError || !authUser) {
          throw new Error("User not found");
        }

        const name =
          authUser.user_metadata?.name ||
          authUser.email?.split("@")[0] ||
          "User";

        const { data: created, error: createError } = await supabase
          .from("users")
          .insert({
            user_id: authUser.id,
            name,
            email: authUser.email,
            administrator: false,
            disabled: false,
          } as never)
          .select("*")
          .single();

        if (createError || !created) {
          throw new Error(createError?.message || "Failed to create user");
        }

        return { data: created };
      }

      return { data: existing };
    }

    if (resource === "contacts" || resource === "clients" || resource === "deals" || resource === "servopa_vendas" || resource === "servopa_vendas_with_details" || resource === "proposal_simulations") {
      const result = await baseDataProvider.getOne(resource, params);
      await assertRecordInScope(resource, result.data);
      return result;
    }

    if (getParentScopeConfig(resource)) {
      const result = await baseDataProvider.getOne(resource, params);
      const scope = await getAccessScope();
      const filteredData = await filterChildRecordsByScope(resource, [result.data], scope);
      if (filteredData.length === 0) {
        throw createAccessDeniedError();
      }
      return { data: filteredData[0] };
    }

    return baseDataProvider.getOne(resource, params);
  },

  async update(resource: string, params: any) {
    if (resource === "users") {
      clearAccessScopeCache();
    }

    if (isScopedMutationResource(resource)) {
      const scope = await getAccessScope();
      const existingRecord = await getExistingRecordForMutation(resource, params.id);
      await assertMutationInScope(resource, scope, existingRecord, existingRecord);
      await assertMutationInScope(resource, scope, params.data, existingRecord);
    }

    return baseDataProvider.update(normalizeResourceForBaseAccess(resource), params);
  },

  async delete(resource: string, params: any) {
    if (resource === "users") {
      clearAccessScopeCache();
    }

    if (isScopedMutationResource(resource)) {
      const scope = await getAccessScope();
      const existingRecord = await getExistingRecordForMutation(resource, params.id);
      await assertMutationInScope(resource, scope, existingRecord, existingRecord);
    }

    return baseDataProvider.delete(normalizeResourceForBaseAccess(resource), params);
  },

  async deleteMany(resource: string, params: any) {
    if (resource === "users") {
      clearAccessScopeCache();
    }

    if (isScopedMutationResource(resource)) {
      const scope = await getAccessScope();

      for (const id of params.ids || []) {
        const existingRecord = await getExistingRecordForMutation(resource, id);
        await assertMutationInScope(resource, scope, existingRecord, existingRecord);
      }
    }

    return baseDataProvider.deleteMany(normalizeResourceForBaseAccess(resource), params);
  },

  async getMany(resource: string, params: any) {
    if (resource === "tags") {
      const authUserId = await getCurrentAuthUserId();
      if (!authUserId || !params.ids || params.ids.length === 0) {
        return { data: [] };
      }

      const { data, error } = await supabase
        .from("tags")
        .select("*")
        .eq("user_id", authUserId)
        .in("id", params.ids);

      if (error) throw error;
      return { data: data || [] };
    }

    if (resource === "users") {
      // Bypass PostgREST: fetch by numeric id directly.
      if (!params.ids || params.ids.length === 0) return { data: [] };
      const { data } = await supabase
        .from("users")
        .select("*")
        .in("id", params.ids);
      return { data: data || [] };
    }

    if (isScopedResource(resource)) {
      const scope = await getAccessScope();
      const result = await baseDataProvider.getMany(resource, params);
      return {
        data: (result.data || []).filter((record: any) => isRecordInScope(resource, record, scope)),
      };
    }

    if (getParentScopeConfig(resource)) {
      const scope = await getAccessScope();
      const result = await baseDataProvider.getMany(resource, params);
      return {
        data: await filterChildRecordsByScope(resource, result.data || [], scope),
      };
    }

    return baseDataProvider.getMany(resource, params);
  },

  async getManyReference(resource: string, params: any) {
    if (resource === "users") {
      return {
        data: [],
        total: 0,
      };
    }

    if (isScopedResource(resource)) {
      const scope = await getAccessScope();
      const scopedParams = {
        ...params,
        filter: getScopedListFilter(resource, params.filter as Record<string, unknown> | undefined, scope),
      };
      return baseDataProvider.getManyReference(resource, scopedParams);
    }

    if (getParentScopeConfig(resource)) {
      const scope = await getAccessScope();
      const result = await baseDataProvider.getManyReference(resource, params);
      const filteredData = await filterChildRecordsByScope(resource, result.data || [], scope);
      return {
        ...result,
        data: filteredData,
        total: filteredData.length,
      };
    }

    return baseDataProvider.getManyReference(resource, params);
  },

  async signUp({ email, password, name }: SignUpData) {
    const response = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          name,
        },
      },
    });

    if (!response.data?.user || response.error) {
      console.error("signUp.error", response.error);
      throw new Error("Failed to create account");
    }

    // Update the is initialized cache
    getIsInitialized._is_initialized_cache = true;

    return {
      id: response.data.user.id,
      email,
      password,
    };
  },
  async salesCreate(body: SalesFormData) {
    const headers = await getAuthHeaders();
    const { data, error } = await supabase.functions.invoke<Sale>("users", {
      method: "POST",
      body,
      headers,
    });

    if (!data || error) {
      console.error("Failed to create account manager", error);
      throw new Error("Failed to create account manager");
    }

    return data;
  },
  async salesUpdate(
    id: Identifier,
    data: Partial<Omit<SalesFormData, "password">>,
  ) {
    const { email, name, administrator, avatar, disabled } = data;

    const headers = await getAuthHeaders();

    const { data: sale, error } = await supabase.functions.invoke<Sale>(
      "users",
      {
        method: "PATCH",
        body: {
          user_id: id,
          email,
          name,
          administrator,
          disabled,
          avatar,
        },
        headers,
      },
    );

    if (!sale || error) {
      console.error("Failed to update user", error);
      throw new Error("Failed to update account manager");
    }

    return data;
  },
  async updatePassword(id: Identifier) {
    console.log("updatePassword called for user:", id);
    const headers = await getAuthHeaders();

    const { data, error } =
      await supabase.functions.invoke("updatePassword", {
        method: "PATCH",
        body: {
          user_id: id,
        },
        headers,
      });

    console.log("updatePassword response:", { data, error });

    if (error) {
      console.error("passwordUpdate.error", error);
      const errorMessage = error.message || error.toString() || "Failed to send password reset email";
      throw new Error(errorMessage);
    }

    // Check if the response indicates an error
    if (data && typeof data === 'object' && 'error' in data) {
      console.error("passwordUpdate.data.error", data.error);
      throw new Error(data.error || "Failed to send password reset email");
    }

    return true;
  },
  async unarchiveDeal(deal: Deal) {
    // get all deals where stage is the same as the deal to unarchive
    const { data: deals } = await baseDataProvider.getList<Deal>("deals", {
      filter: { stage: deal.stage },
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "index", order: "ASC" },
    });

    // set index for each deal starting from 1, if the deal to unarchive is found, set its index to the last one
    const updatedDeals = deals.map((d, index) => ({
      ...d,
      index: d.id === deal.id ? 0 : index + 1,
      archived_at: d.id === deal.id ? null : d.archived_at,
    }));

    return await Promise.all(
      updatedDeals.map((updatedDeal) =>
        baseDataProvider.update("deals", {
          id: updatedDeal.id,
          data: updatedDeal,
          previousData: deals.find((d) => d.id === updatedDeal.id),
        }),
      ),
    );
  },
  async getActivityLog(companyId?: Identifier) {
    return getActivityLog(baseDataProvider, companyId);
  },
  async isInitialized() {
    return getIsInitialized();
  },
} satisfies DataProvider;

export type CrmDataProvider = typeof dataProviderWithCustomMethods;

export const dataProvider = withLifecycleCallbacks(
  dataProviderWithCustomMethods,
  [
    {
      resource: "contactNotes",
      beforeSave: async (data: ContactNote, _, __) => {
        if (data.attachments) {
          for (const fi of data.attachments) {
            await uploadToBucket(fi);
          }
        }
        return data;
      },
    },
    {
      resource: "dealNotes",
      beforeSave: async (data: DealNote, _, __) => {
        if (data.attachments) {
          for (const fi of data.attachments) {
            await uploadToBucket(fi);
          }
        }
        return data;
      },
    },
    {
      resource: "users",
      beforeSave: async (data: Sale, _, __) => {
        if (data.avatar) {
          await uploadToBucket(data.avatar);
        }
        return data;
      },
    },
    {
      resource: "contacts",
      beforeCreate: async (params) => {
        return processContactAvatar(params);
      },
      beforeUpdate: async (params) => {
        return processContactAvatar(params);
      },
      beforeGetList: async (params) => {
        return applyFullTextSearch([
          "details->>name",
          "details->>company",
          "details->>title",
          "details->>email",
          "details->>phone",
          "details->>background",
          "details->>cpf",
        ])(params);
      },
    },
    {
      resource: "companies",
      beforeGetList: async (params) => {
        return applyFullTextSearch([
          "name",
          "phone_number",
          "website",
          "zipcode",
          "city",
          "stateAbbr",
        ])(params);
      },
      beforeCreate: async (params) => {
        const createParams = await processCompanyLogo(params);

        return {
          ...createParams,
          data: {
            ...createParams.data,
            created_at: new Date().toISOString(),
          },
        };
      },
      beforeUpdate: async (params) => {
        return await processCompanyLogo(params);
      },
    },
    {
      resource: "contacts_summary",
      beforeGetList: async (params) => {
        return applyFullTextSearch([
          "details->>name",
          "details->>email",
          "details->>phone",
          "details->>cpf",
        ])(params);
      },
    },
    {
      resource: "users",
      beforeGetList: async (params) => {
        return applyFullTextSearch(["name"])(params);
      },
    },
    {
      resource: "deals",
      beforeGetList: async (params) => {
        return applyFullTextSearch(["name", "type", "description"])(params);
      },
    },
  ],
);

function applyFullTextSearch(columns: string[]) {
  return (params: GetListParams) => {
  if (!params.filter?.q) {
    return params;
  }
  const { q, ...filter } = params.filter;
  return {
    ...params,
    filter: {
      ...filter,
      "@or": columns.reduce((acc, column) => {
        return {
          ...acc,
          [`${column}@ilike`]: q,
        };
      }, {}),
    },
  };
  };
}

const uploadToBucket = async (fi: RAFile) => {
  if (!fi.src.startsWith("blob:") && !fi.src.startsWith("data:")) {
    // Sign URL check if path exists in the bucket
    if (fi.path) {
      const { error } = await supabase.storage
        .from("attachments")
        .createSignedUrl(fi.path, 60);

      if (!error) {
        return;
      }
    }
  }

  const dataContent = fi.src
    ? await fetch(fi.src).then((res) => res.blob())
    : fi.rawFile;

  const file = fi.rawFile;
  const fileExt = file.name.split(".").pop();
  const fileName = `${Math.random()}.${fileExt}`;
  const filePath = await caminhoDoAnexo(fileName);
  const { error: uploadError } = await supabase.storage
    .from("attachments")
    .upload(filePath, dataContent);

  if (uploadError) {
    console.error("uploadError", uploadError);
    throw new Error("Failed to upload attachment");
  }

  const { data } = supabase.storage.from("attachments").getPublicUrl(filePath);

  fi.path = filePath;
  fi.src = data.publicUrl;

  // save MIME type
  const mimeType = file.type;
  fi.type = mimeType;

  return fi;
};
